<template>
	<div class="no_oper_tab report_theme_container" data-theme="no_oper">
		<TabHeader
			title="No Operacionales"
			description="Analiza la tendencia y composición de tus no operacionales"
			:filterPeriod="filterPeriod"
			:filterOptions="filterOptions"
			:endMonth="endMonth"
			:getNow="getNow"
			@update-filter-period="updateFilterPeriod"
			@update-end-month="updateEndMonth">
			<template #center-sub-nav>
				<div class="filters_row is-flex is-align-items-center">
					<div class="sub_nav_filters">
						<button type="button"
							class="filter_btn"
							:class="{ 'is-active': isCuentasMode }"
							@click.stop="setCuentasMode">
							Cuentas
						</button>
					</div>

					<MultiSelectDropdown
						:value="selectedCounterpartTypes"
						:options="counterpartTypeOptions"
						placeholder="Tipos de contraparte"
						@input="SET_SELECTED_COUNTERPART_TYPES" />
				</div>
			</template>
		</TabHeader>

		<!-- ================================================================ -->
		<!-- Summary Cards Section                                             -->
		<!-- Signature del tab: dos progress items OPUESTOS — Ingresos          -->
		<!-- (violet) vs Gastos (coral) — en lugar de la composición           -->
		<!-- monocromática de Costos/Gastos/Activos. El slot #extra muestra    -->
		<!-- "Eficiencia No Operacional" como conclusión inline.               -->
		<!-- Se pliega vía CollapsibleSection; storageKey global sincroniza el -->
		<!-- estado entre todos los tabs de finanzas.                          -->
		<!-- ================================================================ -->
		<CollapsibleSection class="mt-1"
			title="Resumen"
			storageKey="financialReports:summary-expanded">
			<template v-if="summaryHint" #hint>{{ summaryHint }}</template>
			<div class="cards_container is-flex">
				<CardVentas v-for="(card, index) in displayCards" :key="index" v-bind="card" class="mr-4">
					<template #extra>
						<div v-if="card.efficiency" class="efficiency_extra is-flex is-align-items-center">
							<ChartLine class="efficiency_icon mr-1" />
							<span class="efficiency_label">Eficiencia No Operacional</span>
							<span class="efficiency_badge ml-auto">{{ card.efficiency }}</span>
						</div>
					</template>
				</CardVentas>
			</div>
		</CollapsibleSection>

		<!-- Main Table Section -->
		<ExpensesEvolutionTable class="mt-2"
			title="Evolución de No Operacionales"
			emptyMessage="No hay datos de no operacional para el período seleccionado."
			:monthHeaders="monthHeaders"
			:visibleRows="visibleRows"
			:totalRow="totalRow"
			:ratioRow="ratioRow"
			:expandedL2Rows="expandedL2Rows"
			:isLoading="isLoading"
			:error="error"
			:selectedPeriod="selectedPeriodRange"
			:periodOptions="periodOptions"
			:filterPeriod="filterPeriod"
			:showAccumulated="true"
			@toggle-l2="toggleL2"
			@change-period="setPeriodRange"
			@retry="fetchNonOperationalData" />
	</div>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import ChartLine from "@/components/svg/ChartLine.vue";
import MultiSelectDropdown from "@/components/generics/MultiSelectDropdown.vue";
import TabHeader from "@/components/financialReports/TabHeader.vue";
import CollapsibleSection from "@/components/financialReports/CollapsibleSection.vue";
import CardVentas from "@/components/financialReports/CardVentas.vue";
import ExpensesEvolutionTable from "@/components/financialReports/ExpensesEvolutionTable.vue";
import {
	PERIOD_TYPES,
	NON_OPERATIONAL_COUNTERPART_TYPES,
	NON_OPERATIONAL_CARD_PROGRESS_COLORS,
	FILTER_PERIOD_OPTIONS,
} from "@/config/reportStructure";
import { formatPercent, formatRangeLabel } from "@/views/financialReports/utils/periodFormatting";
import { emptyCard } from "@/views/financialReports/utils/summaryCards";
import { parseToMoney } from "@/utils/currency";
import { notificate } from "@/utils/message";

const EMPTY_CARD = () => emptyCard({ efficiency: "" });

export default {
	name: "NoOperTab",

	components: {
		TabHeader,
		CollapsibleSection,
		ChartLine,
		MultiSelectDropdown,
		CardVentas,
		ExpensesEvolutionTable,
	},

	data() {
		return {
			periodOptions: PERIOD_TYPES,
			counterpartTypeOptions: NON_OPERATIONAL_COUNTERPART_TYPES,
			expandedL2Rows: [],
			filterOptions: FILTER_PERIOD_OPTIONS,
		};
	},

	computed: {
		...mapState("reportsFilters", ["endMonth", "selectedPeriodRange"]),
		...mapState("financialReports", ["filterPeriod"]),
		...mapState("nonOperationalReport", [
			"selectedCounterpartTypes",
			"monthHeaders",
			"isLoading",
			"error",
			"summaryCards",
			"summaryError",
		]),
		...mapGetters("nonOperationalReport", ["tableRows", "totalRow", "ratioRow", "isCuentasMode"]),

		getNow() {
			return moment().toDate();
		},

		// Hint compacto al lado del label "Resumen" cuando la sección está
		// plegada — preserva el monto del mes actual para no perder contexto.
		summaryHint() {
			const amount = this.summaryCards && this.summaryCards.current_month
				? this.summaryCards.current_month.amount
				: null;
			return amount != null ? parseToMoney(amount) : "";
		},

		visibleRows() {
			return this.tableRows.filter(row => {
				if (row.level === 2) return true;
				return this.expandedL2Rows.includes(row.parent);
			});
		},

		displayCards() {
			if (!this.summaryCards) {
				return [EMPTY_CARD(), EMPTY_CARD(), EMPTY_CARD()];
			}
			const { current_month, ytd, ltm } = this.summaryCards;
			return [
				this.buildCard(current_month, { showMom: true }),
				this.buildCard(ytd),
				this.buildCard(ltm),
			];
		},

		fetchSignature() {
			return [
				this.endMonth,
				this.selectedPeriodRange,
				this.selectedCounterpartTypes.join(","),
				this.filterPeriod,
			].join("|");
		},

		summarySignature() {
			return moment(this.endMonth).format("YYYY-MM");
		},
	},

	methods: {
		...mapActions("nonOperationalReport", ["fetchNonOperationalData", "fetchNonOperationalSummary"]),
		...mapActions("reportsFilters", ["setPeriodRange"]),
		...mapMutations("reportsFilters", ["SET_END_MONTH"]),
		...mapMutations("nonOperationalReport", [
			"SET_SELECTED_COUNTERPART_TYPES",
		]),
		...mapMutations("financialReports", ["SET_FILTER_PERIOD"]),

		updateEndMonth(date) {
			this.SET_END_MONTH(date);
		},
		updateFilterPeriod(period) {
			this.SET_FILTER_PERIOD(period);
		},
		setCuentasMode() {
			this.SET_SELECTED_COUNTERPART_TYPES([]);
		},
		toggleL2(label) {
			const index = this.expandedL2Rows.indexOf(label);
			if (index > -1) {
				this.expandedL2Rows.splice(index, 1);
			} else {
				this.expandedL2Rows.push(label);
			}
		},

		buildCard(card, { showMom = false } = {}) {
			return {
				period: formatRangeLabel(card.period_start, card.period_end),
				amount: card.amount,
				mom: showMom ? formatPercent(card.mom) : "",
				yoy: formatPercent(card.yoy),
				efficiency: card.efficiency != null ? formatPercent(card.efficiency) : "",
				progressItems: (card.breakdown || []).map((item, i) => ({
					label: item.label,
					percent: item.percent,
					color: NON_OPERATIONAL_CARD_PROGRESS_COLORS[i % NON_OPERATIONAL_CARD_PROGRESS_COLORS.length],
				})),
			};
		},
	},

	mounted() {
		this.fetchNonOperationalData();
		this.fetchNonOperationalSummary();
	},

	watch: {
		fetchSignature() { this.fetchNonOperationalData(); },
		summarySignature() { this.fetchNonOperationalSummary(); },
		// El fetch del resumen falla en silencio (las cards quedan vacías). Un
		// toast hace visible el error sin romper el layout de las cards.
		summaryError(message) {
			if (message) notificate(message, "is-danger is-light", "is-top-right");
		},
	},
};
</script>

<style scoped lang="scss">
.no_oper_tab {
	position: relative;
	padding-bottom: 40px;
	height: 100%;
}

// Header común vive en TabHeader.vue. Replicamos las classes del slot
// #center-sub-nav (botón Cuentas + MultiSelectDropdown) localmente porque
// TabHeader es scoped y sus estilos no llegan al markup inyectado al slot.

.filters_row {
	gap: 12px;
}

.sub_nav_filters {
	display: inline-flex;
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	width: fit-content;
	box-sizing: border-box;
	min-height: 34px;
}

.filter_btn {
	display: inline-flex;
	align-items: center;
	padding: 0 16px;
	min-height: 32px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	line-height: 1;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	transition: all 0.15s ease;
	box-sizing: border-box;

	&:hover { background: var(--report-surface); }

	&.is-active {
		background: var(--report-primary);
		color: var(--report-bg);
	}
}

.cards_container {
	width: 100%;
}

// "Eficiencia No Operacional X%" — signature inline del tab. Mismo patrón
// visual que Margen Bruto (Costos) y Eficiencia (Gastos): icon+label a la
// izquierda y el porcentaje en una pill empujada a la derecha con ml-auto.
.efficiency_extra {
	width: 100%;
	font-size: 13px;
}

.efficiency_icon {
	width: 14px;
	height: 14px;
	color: var(--report-text-muted-soft);
}

.efficiency_label {
	color: var(--report-text-muted);
	font-weight: 500;
}

.efficiency_badge {
	display: inline-flex;
	align-items: center;
	padding: 4px 10px;
	border-radius: 999px;
	background: var(--report-accent-bg);
	color: var(--report-accent-text);
	border: 1px solid var(--report-accent-border);
	font-size: 12px;
	font-weight: 700;
}
</style>
